/**
 * ProveIdeaPage — Wizard 2.
 *
 * Step 1: textarea + optional file attachments.
 * Step 2: GiOrbit spinner while we hash the canonical content.
 * Step 3: "Done!" — first line of the idea, timestamp, hash, download proof.
 *
 * The hash is computed over the canonical JSON `{ text, attachments[]
 * (name, size, hash) }`. Files contribute their own SHA-256 to the
 * canonical record so the proof binds both the text and the files.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Download, Paperclip, X } from 'lucide-react';

import { Button } from '@/components/Button';
import { GiOrbitLogo } from '@/components/GiOrbitLogo';
import { WizardShell } from '@/layouts/WizardShell';
import { useAuthStore } from '@/stores/authStore';
import { useProofsStore, buildProofCertificate, type Proof } from '@/stores/proofsStore';
import { useCreateProof } from '@/hooks/useProofs';
import { sha256Hex, sha256OfFile, downloadJson, shortHash } from '@/utils/hashing';
import { cn } from '@/utils/cn';

type Step = 'compose' | 'signing' | 'done';

interface Attachment {
  file: File;
  hash?: string;
}

export function ProveIdeaPage() {
  const { t } = useTranslation('wizards');
  const user = useAuthStore((s) => s.user);
  const addProof = useProofsStore((s) => s.add);
  const createProof = useCreateProof();

  const [step, setStep] = useState<Step>('compose');
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [proof, setProof] = useState<Proof | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stepNum = step === 'compose' ? 1 : step === 'signing' ? 2 : 3;

  const handleSign = async () => {
    setError(null);
    if (!text.trim()) {
      setError(t('prove_idea.no_text'));
      return;
    }
    setStep('signing');
    try {
      const attachmentRecords = await Promise.all(
        attachments.map(async (a) => ({
          name: a.file.name,
          size: a.file.size,
          hash: await sha256OfFile(a.file),
        })),
      );
      const canonical = JSON.stringify({ text: text.trim(), attachments: attachmentRecords });
      const hash = await sha256Hex(canonical);
      const firstLine = text.trim().split(/\r?\n/)[0]?.slice(0, 80) || 'Idea';
      const created = addProof({
        kind: 'idea',
        name: firstLine,
        hash,
        signer: user?.email ?? 'anonymous',
      });
      // See SignFilePage for the fire-and-forget rationale.
      createProof.mutate(created);
      await new Promise((r) => setTimeout(r, 700));
      setProof(created);
      setStep('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unknown_error');
      setStep('compose');
    }
  };

  const reset = () => {
    setText('');
    setAttachments([]);
    setProof(null);
    setStep('compose');
  };

  return (
    <WizardShell title={t('prove_idea.page_title')} totalSteps={3} currentStep={stepNum}>
      {step === 'compose' ? (
        <ComposeStep
          text={text}
          setText={setText}
          attachments={attachments}
          setAttachments={setAttachments}
          onSubmit={handleSign}
          error={error}
        />
      ) : step === 'signing' ? (
        <SigningStep />
      ) : (
        <DoneStep proof={proof} onAnother={reset} />
      )}
    </WizardShell>
  );
}

function ComposeStep({
  text,
  setText,
  attachments,
  setAttachments,
  onSubmit,
  error,
}: {
  text: string;
  setText: (v: string) => void;
  attachments: Attachment[];
  setAttachments: (a: Attachment[]) => void;
  onSubmit: () => void;
  error: string | null;
}) {
  const { t } = useTranslation('wizards');

  const onAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setAttachments([...attachments, ...files.map((f) => ({ file: f }))]);
    e.target.value = '';
  };
  const removeAt = (i: number) => setAttachments(attachments.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="idea-text" className="mb-1 block text-sm font-medium text-ink">
          {t('prove_idea.label')}
        </label>
        <textarea
          id="idea-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('prove_idea.placeholder')}
          rows={8}
          className="block w-full rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-spark focus:outline-none"
        />
      </div>

      <div>
        <label htmlFor="idea-attach" className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink hover:bg-surface-raised">
          <Paperclip className="h-4 w-4" aria-hidden />
          {t('prove_idea.attach')}
          <input id="idea-attach" type="file" multiple className="sr-only" onChange={onAttach} />
        </label>
      </div>

      {attachments.length > 0 ? (
        <ul className="space-y-1">
          {attachments.map((a, i) => (
            <li key={i} className="flex items-center justify-between rounded-md border border-border bg-canvas px-3 py-2 text-sm">
              <span className="truncate">{a.file.name}</span>
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="rounded p-1 text-ink-subtle hover:bg-surface-raised hover:text-ink"
                aria-label="remove"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {error ? (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <div className="flex justify-end">
        <Button onClick={onSubmit} size="lg">
          {t('prove_idea.page_title')}
        </Button>
      </div>
    </div>
  );
}

function SigningStep() {
  const { t } = useTranslation('wizards');
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-12">
      <GiOrbitLogo size="lg" />
      <p className="text-base font-medium">{t('prove_idea.spinning')}</p>
    </div>
  );
}

function DoneStep({ proof, onAnother }: { proof: Proof | null; onAnother: () => void }) {
  const { t, i18n } = useTranslation('wizards');
  const [last, setLast] = useState(proof);
  useEffect(() => { if (proof) setLast(proof); }, [proof]);
  if (!last) return null;

  const handleDownload = () => {
    const cert = buildProofCertificate(last);
    downloadJson(`${slug(last.name)}.proof.json`, cert);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center gap-3 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-success/15 text-success">
          <Check className="h-7 w-7" aria-hidden />
        </span>
        <h2 className="text-xl font-semibold">{t('common.done_title')}</h2>
        <p className="text-sm text-ink-muted">{t('common.done_idea')}</p>
      </div>

      <dl className="space-y-3 rounded-lg border border-border bg-canvas p-4 text-sm">
        <Row label="" value={last.name} />
        <Row label={t('common.timestamp')} value={new Date(last.timestamp).toLocaleString(i18n.language)} />
        <Row label={t('common.hash_short')} value={shortHash(last.hash, 12, 12)} mono />
        <Row label={t('common.signer')} value={last.signer} />
      </dl>

      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button variant="secondary" onClick={onAnother}>
          {t('common.another_idea')}
        </Button>
        <Button onClick={handleDownload} leading={<Download className="h-4 w-4" aria-hidden />}>
          {t('common.download_proof')}
        </Button>
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
      <dt className="text-xs uppercase tracking-wider text-ink-subtle">{label}</dt>
      <dd className={cn('whitespace-pre-wrap break-words text-sm text-ink', mono && 'font-mono break-all')}>
        {value}
      </dd>
    </div>
  );
}

function slug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9-_]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64) || 'idea';
}
